import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, InputNumber, Radio, Select, Space, Spin, Typography, message } from 'antd';
import { errorMessageWithFieldErrors, formatDateTime, space, useNarrowViewport } from '@viq-admin/ui';
import {
  LEAD_FIELDS,
  NOTES_MIN_CHARS_MAX,
  type LeadEnforcement,
  type LeadRequirement,
  type LeadRulesResponse,
} from '@viq-admin/types';
import { LEAD_RULES_COPY } from './copy';
import { coverageLine, LEAD_FIELD_LABELS } from './labels';
import {
  addableTypes,
  blockWithoutNotes,
  draftFromResponse,
  isDraftDirty,
  newRequirement,
  removeRequirement,
  setRequirementHold,
  toSaveInput,
  updateRequirement,
  validateDraft,
  type LeadRulesDraft,
} from './editorModel';
import { isConflictError, type LeadRulesApi, type LeadRulesInventoryQuestion } from './types';

export interface LeadRulesEditorProps {
  /** Shell-injected (closure-only). */
  api: LeadRulesApi;
  /** Portal: company_admin. Console: CASL `update Company`. */
  canEdit: boolean;
  /**
   * Console only (spec 2026-09-28 §6): show each field requirement's "Hold
   * the lead until filled" checkbox. The portal never passes it, and its
   * saves keep the stored holds server-side.
   */
  canSetHold?: boolean;
  /** Requirement id factory; defaults to crypto.randomUUID. Tests inject a deterministic one. */
  newId?: () => string;
}

const ENFORCEMENTS: LeadEnforcement[] = ['off', 'flag', 'block'];
const defaultNewId = () => crypto.randomUUID();

/**
 * Company lead rules editor (viq-vault spec 2026-09-14-company-lead-rules-design
 * §3.3). Used by the console's company Lead rules tab and the portal's Company
 * settings page. Saves on its own button, independent of any page-level Edit.
 */
export function LeadRulesEditor({ api, canEdit, canSetHold = false, newId = defaultNewId }: LeadRulesEditorProps) {
  const [saved, setSaved] = useState<LeadRulesResponse | null>(null);
  const narrow = useNarrowViewport();
  // antd sizes a Space item to its content, so a long question would push the
  // row past a phone's or tablet's edge; capping each item at its row lets the
  // select's own ellipsis take over. (No effect where the row already fits.)
  const fitItems = { item: { maxWidth: '100%' } };
  const [draft, setDraft] = useState<LeadRulesDraft | null>(null);
  const [inventory, setInventory] = useState<LeadRulesInventoryQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setConflict(false);
    try {
      const [rules, questions] = await Promise.all([api.get(), api.listInventoryQuestions()]);
      setSaved(rules);
      setDraft(draftFromResponse(rules));
      setInventory(questions);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = !!saved && !!draft && isDraftDirty(draft, saved);
  const issues = useMemo(() => (draft ? validateDraft(draft) : []), [draft]);

  // Cover a real browser navigation while there are unsaved rule edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  if (loading) {
    return (
      <div data-testid="lead-rules-loading">
        <Spin />
      </div>
    );
  }

  if (loadFailed || !saved || !draft) {
    return (
      <Alert
        type="error"
        showIcon
        message={LEAD_RULES_COPY.loadFailed}
        action={
          <Button size="small" onClick={() => void load()}>
            {LEAD_RULES_COPY.retry}
          </Button>
        }
      />
    );
  }

  const currentDraft = draft;
  const questionText = new Map(inventory.map((q) => [q.id, q.text]));

  async function handleSave() {
    setSaving(true);
    try {
      const next = await api.save(toSaveInput(currentDraft, saved!.version));
      setSaved(next);
      setDraft(draftFromResponse(next));
      message.success(LEAD_RULES_COPY.saved);
    } catch (err) {
      if (isConflictError(err)) setConflict(true);
      else message.error(errorMessageWithFieldErrors(err, LEAD_RULES_COPY.saveFailed));
    } finally {
      setSaving(false);
    }
  }

  function renderControl(requirement: LeadRequirement) {
    switch (requirement.type) {
      case 'notes_min_chars':
        return (
          <Space wrap>
            <Typography.Text strong>{LEAD_RULES_COPY.addType.notes_min_chars}</Typography.Text>
            <InputNumber
              aria-label={LEAD_RULES_COPY.addType.notes_min_chars}
              min={1}
              max={NOTES_MIN_CHARS_MAX}
              precision={0}
              value={requirement.min}
              disabled={!canEdit}
              onChange={(value) =>
                setDraft(updateRequirement(currentDraft, requirement.id, { min: typeof value === 'number' ? value : 0 }))
              }
            />
            <Typography.Text type="secondary">{LEAD_RULES_COPY.notesSuffix}</Typography.Text>
          </Space>
        );
      case 'question_answered': {
        const coverage = saved!.coverage[requirement.id];
        const known = questionText.has(requirement.questionId);
        if (coverage?.questionDeleted || !known) {
          return (
            <Space direction="vertical" size={space.xs}>
              <Typography.Text strong>{LEAD_RULES_COPY.addType.question_answered}</Typography.Text>
              <Typography.Text type="warning">{LEAD_RULES_COPY.questionDeleted}</Typography.Text>
            </Space>
          );
        }
        const takenByOthers = new Set(
          currentDraft.requirements.flatMap((r) =>
            r.type === 'question_answered' && r.id !== requirement.id ? [r.questionId] : [],
          ),
        );
        return (
          <Space direction="vertical" size={space.xs} style={{ width: '100%' }} styles={fitItems}>
            {/* Stacked on a phone: the select is as wide as its question text,
                which a phone can't fit beside the label. */}
            <Space
              wrap
              direction={narrow ? 'vertical' : 'horizontal'}
              style={narrow ? { width: '100%' } : undefined}
              styles={fitItems}
            >
              <Typography.Text strong>{LEAD_RULES_COPY.addType.question_answered}</Typography.Text>
              <Select
                aria-label={LEAD_RULES_COPY.questionSelectLabel}
                style={narrow ? { width: '100%' } : { minWidth: 240, maxWidth: '100%' }}
                value={requirement.questionId}
                disabled={!canEdit}
                options={inventory
                  .filter((q) => !takenByOthers.has(q.id))
                  .map((q) => ({ value: q.id, label: q.text }))}
                onChange={(questionId: string) =>
                  setDraft(updateRequirement(currentDraft, requirement.id, { questionId }))
                }
              />
            </Space>
            <Typography.Text type="secondary">
              {coverage && saved!.requirements.some((r) => r.id === requirement.id && r.type === 'question_answered' && r.questionId === requirement.questionId)
                ? coverageLine(coverage)
                : LEAD_RULES_COPY.coveragePending}
            </Typography.Text>
          </Space>
        );
      }
      case 'field_present': {
        const takenByOthers = new Set(
          currentDraft.requirements.flatMap((r) => (r.type === 'field_present' && r.id !== requirement.id ? [r.field] : [])),
        );
        return (
          <Space wrap>
            <Typography.Text strong>{LEAD_RULES_COPY.addType.field_present}</Typography.Text>
            <Select
              aria-label={LEAD_RULES_COPY.fieldSelectLabel}
              style={{ minWidth: 180 }}
              value={requirement.field}
              disabled={!canEdit}
              options={LEAD_FIELDS.filter((f) => !takenByOthers.has(f)).map((f) => ({ value: f, label: LEAD_FIELD_LABELS[f] }))}
              onChange={(field: (typeof LEAD_FIELDS)[number]) =>
                setDraft(updateRequirement(currentDraft, requirement.id, { field }))
              }
            />
            {canSetHold && (
              <Checkbox
                checked={requirement.hold === true}
                disabled={!canEdit || currentDraft.enforcement !== 'block'}
                onChange={(e) => setDraft(setRequirementHold(currentDraft, requirement.id, e.target.checked))}
              >
                {LEAD_RULES_COPY.holdLabel}
              </Checkbox>
            )}
          </Space>
        );
      }
    }
  }

  const addable = canEdit ? addableTypes(currentDraft, inventory) : [];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="lead-rules-editor">
      {conflict && (
        <Alert
          type="error"
          showIcon
          message={LEAD_RULES_COPY.conflictTitle}
          description={LEAD_RULES_COPY.conflictBody}
          action={
            <Button size="small" onClick={() => void load()}>
              {LEAD_RULES_COPY.reload}
            </Button>
          }
        />
      )}

      {saved.updatedBy && saved.updatedAt && (
        <Typography.Text type="secondary">
          {LEAD_RULES_COPY.lastSaved(saved.updatedBy.name ?? saved.updatedBy.oid, formatDateTime(saved.updatedAt))}
        </Typography.Text>
      )}

      <section>
        <Typography.Title level={5}>{LEAD_RULES_COPY.enforcementLabel}</Typography.Title>
        <Radio.Group
          value={currentDraft.enforcement}
          disabled={!canEdit}
          onChange={(e) => setDraft({ ...currentDraft, enforcement: e.target.value as LeadEnforcement })}
        >
          <Space direction="vertical">
            {ENFORCEMENTS.map((mode) => (
              <Radio key={mode} value={mode}>
                <Typography.Text strong>{LEAD_RULES_COPY.enforcement[mode].label}</Typography.Text>{' '}
                <Typography.Text type="secondary">{LEAD_RULES_COPY.enforcement[mode].help}</Typography.Text>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
        {blockWithoutNotes(currentDraft) && (
          <Alert style={{ marginTop: space.md }} type="warning" showIcon message={LEAD_RULES_COPY.blockWithoutNotes} />
        )}
      </section>

      <section>
        <Typography.Title level={5}>{LEAD_RULES_COPY.requirementsLabel}</Typography.Title>
        {canSetHold && (
          <Typography.Paragraph type="secondary">{LEAD_RULES_COPY.holdHelp}</Typography.Paragraph>
        )}
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {currentDraft.requirements.length === 0 && (
            <Typography.Text type="secondary">{LEAD_RULES_COPY.empty}</Typography.Text>
          )}
          {currentDraft.requirements.map((requirement) => {
            const rowIssues = issues.filter((i) => i.requirementId === requirement.id);
            return (
              <Card key={requirement.id} size="small" data-testid={`lead-rule-${requirement.id}`}>
                {/* Remove drops below the control on a phone instead of running
                    off it. */}
                <Space
                  align="start"
                  direction={narrow ? 'vertical' : 'horizontal'}
                  // Where it is still a row, Remove wraps below rather than
                  // off the edge.
                  wrap={!narrow}
                  style={{ width: '100%', justifyContent: 'space-between' }}
                  styles={fitItems}
                >
                  {renderControl(requirement)}
                  {canEdit && (
                    <Button type="text" danger onClick={() => setDraft(removeRequirement(currentDraft, requirement.id))}>
                      {LEAD_RULES_COPY.remove}
                    </Button>
                  )}
                </Space>
                {rowIssues.map((issue) => (
                  <Typography.Text key={issue.message} type="danger" style={{ display: 'block' }}>
                    {issue.message}
                  </Typography.Text>
                ))}
              </Card>
            );
          })}
          {canEdit && (
            <Space wrap>
              <Typography.Text>{LEAD_RULES_COPY.addRequirement}</Typography.Text>
              {addable.map((type) => (
                <Button
                  key={type}
                  onClick={() =>
                    setDraft({
                      ...currentDraft,
                      requirements: [...currentDraft.requirements, newRequirement(type, currentDraft, inventory, newId)],
                    })
                  }
                >
                  {`+ ${LEAD_RULES_COPY.addType[type]}`}
                </Button>
              ))}
            </Space>
          )}
        </Space>
      </section>

      {canEdit && (
        <Space>
          <Button
            type="primary"
            loading={saving}
            disabled={!dirty || issues.length > 0}
            onClick={() => void handleSave()}
          >
            {LEAD_RULES_COPY.save}
          </Button>
          <Button disabled={!dirty || saving} onClick={() => setDraft(draftFromResponse(saved))}>
            {LEAD_RULES_COPY.discard}
          </Button>
        </Space>
      )}
    </Space>
  );
}
